import React, { CSSProperties } from 'react';
import { makeStyles } from 'tss-react/mui';
import { PDFDocumentProxy } from 'pdfjs-dist';
import { useQuery } from '@tanstack/react-query';


const useStyles = makeStyles()({
  root: {
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
  },
  canvas: {
    width: '100%',
  }
});


type Point = {
  x: number;
  y: number;
};

const getPointsMaxDiff = (a: Point, b: Point) => {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
};

const getLimitedPoint = (point: number, limit: number) => {
  return Math.abs(point) > limit ? limit * Math.sign(point) : point;
};


const useZoomAndDrag = (enabled = false) => {
  const [ scale, setScale ] = React.useState(1);
  const [ point, setPoint ] = React.useState<Point>({ x: 0, y: 0 });
  const [ draggingState, setDraggingState ] = React.useState<'none' | 'down' | 'dragging'>('none');

  const startPointRef = React.useRef<Point | null>({ x: 0, y: 0 });
  const movePointRef = React.useRef<Point>({ x: 0, y: 0 });
  const dragLimitRef = React.useRef<Point>({ x: 0, y: 0 });

  const draggingStateRef = React.useRef(draggingState);
  const scaleRef = React.useRef(scale);
  const pointRef = React.useRef(point);

  draggingStateRef.current = draggingState;
  scaleRef.current = scale;
  pointRef.current = point;

  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    const onMouseMove = (e: MouseEvent) => {
      movePointRef.current = {
        x: e.clientX,
        y: e.clientY,
      };

      if (scaleRef.current === 1) {
        return;
      }

      if (startPointRef.current) {
        const diff = getPointsMaxDiff(movePointRef.current, startPointRef.current);

        if (draggingStateRef.current === 'down' && diff > 5) {
          setDraggingState('dragging');
        }
      }

      if (draggingStateRef.current === 'dragging') {
        setPoint(point => {
          const pointX = point.x + e.movementX;
          const pointY = point.y + e.movementY;

          return {
            x: getLimitedPoint(pointX, dragLimitRef.current.x),
            y: getLimitedPoint(pointY, dragLimitRef.current.y),
          };
        });
      }
    };

    const onMouseUp = (e: MouseEvent) => {
      if (draggingStateRef.current === 'down') {
        if (scaleRef.current === 1) {
          if (e.target instanceof HTMLElement) {
            const bounds = e.target.getBoundingClientRect();

            setPoint({
              x: (e.offsetX - bounds.width / 2) * -1,
              y: (e.offsetY - bounds.height / 2) * -1,
            });
          } else {
            setPoint({ x: 0, y: 0 });
          }
          setScale(2);
        } else {
          setScale(1);
          setPoint({ x: 0, y: 0 });
        }
      }
      setDraggingState('none');
      startPointRef.current = null;
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [ enabled ]);

  const handlers = React.useMemo(() => {
    if (!enabled) {
      return {};
    }

    const onMouseDown: React.MouseEventHandler<HTMLCanvasElement> = (e) => {
      // only handle click by left mouse button click
      if (e.button !== 0) {
        return;
      }

      startPointRef.current = {
        x: e.clientX,
        y: e.clientY,
      };

      setDraggingState('down');
      if (e.target instanceof HTMLElement) {
        const rect = e.target.getBoundingClientRect();

        dragLimitRef.current = { x: rect.width / 4, y: rect.height / 4 };
      }
    };

    return {
      onMouseDown,
    };
  }, [ enabled ]);

  const cursor: CSSProperties['cursor'] = React.useMemo(() => {
    if (!enabled) {
      return undefined;
    }

    if (draggingState === 'dragging') {
      return 'grabbing';
    }

    if (scale === 1) {
      return 'zoom-in';
    }

    if (scale === 2) {
      return 'zoom-out';
    }

    return undefined;
  }, [ scale, point, draggingState ]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    handlers,
    scale,
    point,
    cursor,
    draggingState,
    reset: () => {
      setPoint({ x: 0, y: 0 });
      setScale(1);
    }
  };
};


export const PDFPage: React.VFC<{
  pdf: PDFDocumentProxy;
  pageNumber?: number;
  zoomAndDrag?: boolean;
}> = ({ pdf, pageNumber = 0, zoomAndDrag = false }) => {
  const [ canvas, setCanvas ] = React.useState<HTMLCanvasElement | null>(null);
  const zoomAndDragHelpers = useZoomAndDrag(zoomAndDrag);
  const { classes } = useStyles();

  React.useEffect(() => {
    zoomAndDragHelpers.reset();
  }, [ pdf, pageNumber ]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: page } = useQuery({
    queryKey: [ 'pdf-page', pdf._pdfInfo, pageNumber ],
    queryFn: async() => {
      const page = await pdf.getPage(pageNumber + 1);
      const outputScale = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: outputScale });

      const tmpCanvas = document.createElement('canvas');
      const context = tmpCanvas.getContext('2d');

      if (!context) {
        return;
      }

      const styles = {
        width: Math.floor(viewport.width * outputScale),
        height: Math.floor(viewport.height * outputScale),
        aspectRatio: `${viewport.width / viewport.height}`,
      };

      tmpCanvas.width = styles.width;
      tmpCanvas.height = styles.height;

      const transform = outputScale !== 1
        ? [ outputScale, 0, 0, outputScale, 0, 0 ]
        : null;

      await page.render({
        canvasContext: context,
        viewport: viewport,
        transform: transform ?? undefined,
      }).promise;

      return {
        canvas: context.canvas,
        styles,
      };
    },
    suspense: true
  });

  React.useEffect(() => {
    if (!page?.canvas) {
      return;
    }

    const context = canvas?.getContext('2d');

    if (!context) {
      return;
    }

    context.drawImage(page.canvas, 0, 0);
  }, [ canvas, page ]);

  return (
    <div
      className={classes.root}
      style={{ aspectRatio: page?.styles.aspectRatio ?? '0.7' }}
    >
      <canvas
        {...zoomAndDragHelpers.handlers}
        width={page?.styles.width}
        height={page?.styles.height}
        style={{
          aspectRatio: page?.styles.aspectRatio ?? '0.7',
          cursor: zoomAndDragHelpers.cursor,
          transform: `translate(${zoomAndDragHelpers.point?.x ?? 0}px, ${zoomAndDragHelpers.point?.y ?? 0}px) scale(${zoomAndDragHelpers.scale})`,
          transition: zoomAndDragHelpers.draggingState === 'dragging' ? 'none' : 'transform 120ms ease-in-out',
        }}
        className={classes.canvas}
        ref={setCanvas}
      >
        Not supported
      </canvas>
    </div>
  );
};
